import {
	AgentCapacity,
	createLog,
	INJECTED_FEE_CODES,
	LogSeverity,
	OPERATIONS_SECTIONS,
	PricingModel,
	Services,
	SETTINGS_GROUPS,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';
import { servicesProviderSaleServiceDtoSchema } from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import type { ServicesProvider } from '@ekhodealer/ekho-common';
import type {
	ServicesProviderSaleServiceDtoInput,
	ServicesProviderSaleServiceDtoOutput,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useCreateSaleService } from '../../../api/configuration/services/useCreateSaleService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';
import { Subsection } from '../../config-mgmt/Subsection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): ServicesProviderSaleServiceDtoInput {
	// TODO: This class instantiation is breaking in prod due to `Unknown subclass: sale` error.
	// Instantiate ServicesProvider class from plain data
	// const servicesProviderInstance = servicesProvider
	// 	? new ServicesProvider(servicesProvider)
	// 	: null;
	// const saleServicesProvider = servicesProviderInstance?.toServicesProviderForService(Services.SALE);
	// const saleConfig = saleServicesProvider?.config[Services.SALE];
	const saleConfig = servicesProvider?.config[Services.SALE];

	// Get primary contract signer (ADMIN agent)
	// @ts-expect-error - TODO: we need to solve services provider class instantiation issue to type narrow
	const primarySigner = servicesProvider?.agents.find(
		// @ts-expect-error - TODO: we need to solve services provider class instantiation issue to type narrow
		(agent) => agent.capacity === AgentCapacity.ADMIN,
	);

	// Get pricing array (default to FLAT_FEE with 0 if not present)
	const defaultPricing = {
		model: PricingModel.FLAT_FEE as const,
		modelDetails: {
			flatFee: 0,
		},
	};

	const pricing = saleConfig?.pricing?.length ? saleConfig.pricing : [defaultPricing];

	return {
		supported: saleConfig?.supported ?? false,
		licenseNumber: saleConfig?.licenseNumber || '',
		pricing: [pricing[0] as typeof defaultPricing], // Use first pricing entry
		primaryContractSigner: primarySigner
			? {
					firstName: primarySigner.firstName || '',
					middleName: primarySigner.middleName || '',
					lastName: primarySigner.lastName || '',
					email: primarySigner.email || '',
				}
			: undefined,
	};
}

export function SaleServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const createSaleServiceMutation = useCreateSaleService();

	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.SALE);

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: ServicesProviderSaleServiceDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		// Transform DTO to API payload format
		const dataToSubmit = {
			supported: data.supported,
			licenseNumber: data.licenseNumber,
			pricing: [
				{
					...data.pricing[0],
					code: INJECTED_FEE_CODES.DOCUMENT_FEE,
					feeDescription: 'Document fee',
				},
			],
			primaryContractSigner: data.primaryContractSigner,
		};

		try {
			await createSaleServiceMutation.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update sale service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{ group: SETTINGS_GROUPS.OPERATIONS, section: OPERATIONS_SECTIONS.SALE }}
			form={{
				schema: servicesProviderSaleServiceDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Sale'
			description='Configure your doc fee charged to buyers at checkout.'
			formTitle='Sale configuration'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'disabled',
				helpText:
					'This service is managed by Ekho. Contact your account manager to make changes.',
			}}
			hideDivider>
			<Form.Field.Text
				schemaField='licenseNumber'
				labelText='License Number'
				descriptionText='Your dealer license number, included on sales documents.'
				required
				size='large'
				placeholder='Enter your License Number'
			/>
			<Form.Field.Pricing
				schemaField='pricing'
				supportedModels={[PricingModel.FLAT_FEE]}
				modelLabelText='Document Fee'
				modelDescriptionText='Your standard document processing fee. Should match your existing doc fee.'
			/>
			<Subsection
				group={SETTINGS_GROUPS.OPERATIONS}
				section={OPERATIONS_SECTIONS.SALE}
				subsection={SUBSECTIONS.SALES_PRIMARY_CONTRACT_SIGNER}
				title='Primary Contract Signer'
				description='The person responsible for signing sales contracts.'>
				<Form.Field.Text
					schemaField='primaryContractSigner.firstName'
					labelText='First Name'
					required
					size='large'
					placeholder='First Name'
					variant='filled'
				/>
				<Form.Field.Text
					schemaField='primaryContractSigner.middleName'
					labelText='Middle Name'
					size='large'
					placeholder='Middle Name (Optional)'
					variant='filled'
				/>
				<Form.Field.Text
					schemaField='primaryContractSigner.lastName'
					labelText='Last Name'
					required
					size='large'
					placeholder='Last Name'
					variant='filled'
				/>
				<Form.Field.Text
					schemaField='primaryContractSigner.email'
					labelText='Email'
					required
					size='large'
					type='email'
					placeholder='email@example.com'
					variant='filled'
				/>
			</Subsection>
		</FormSection>
	);
}
